import { useEffect } from "react";

/**
 * usePageMeta — sets document.title and meta tags for SEO.
 * Cleans up on unmount, restoring defaults.
 *
 * @param {Object} options
 * @param {string} [options.title] - Page title
 * @param {string} [options.description] - Meta description
 * @param {string} [options.image] - OG image URL
 * @param {string} [options.canonicalPath] - Path for canonical URL (e.g. /academy/slug/courses)
 * @param {string} [options.siteName] - OG site name (academy name)
 * @param {string} [options.type] - OG type (default: "website")
 */
export default function usePageMeta({
  title,
  description,
  image,
  canonicalPath,
  canonicalUrl,
  siteName,
  type = "website",
  favicon,
  locale,
  alternates,
  keywords,
  noindex,
  jsonLd,
  themeColor,
} = {}) {
  useEffect(() => {
    const originalTitle = document.title;
    const setMeta = (name, content) => {
      if (!content) return;
      const attr = name.startsWith("og:") || name.startsWith("twitter:")
        ? "property"
        : "name";
      // og: uses property, twitter: uses name actually — but both work
      let el = document.querySelector(`meta[${attr}="${name}"]`)
        || document.querySelector(`meta[name="${name}"]`)
        || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        el._created = true;
      }
      el._prev = el._prev ?? el.getAttribute("content");
      el.setAttribute("content", content);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        el._created = true;
      }
      el._prev = el._prev ?? el.getAttribute("href");
      el.setAttribute("href", href);
    };

    // Set title
    if (title) {
      document.title = title;
    }

    // Set meta tags
    setMeta("description", description);
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("og:image", image);
    setMeta("og:type", type);
    setMeta("og:site_name", siteName);
    setMeta("twitter:card", image ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    // Canonical URL
    // `canonicalUrl` (full https URL) wins over `canonicalPath` so callers
    // can pin canonical/og:url to a different origin (e.g. an academy's
    // verified custom domain) regardless of which host served the request.
    let canonicalHref = null;
    if (canonicalUrl) {
      canonicalHref = String(canonicalUrl);
    } else if (canonicalPath) {
      const base = window.location.origin;
      const path = String(canonicalPath).startsWith("/")
        ? canonicalPath
        : `/${canonicalPath}`;
      canonicalHref = `${base}${path}`;
    }
    if (canonicalHref) {
      setLink("canonical", canonicalHref);
      setMeta("og:url", canonicalHref);
    }

    // Favicon (icon + apple-touch-icon)
    // We must remove ALL existing icon links because index.html ships with
    // a hardcoded <link rel="icon" type="image/png" href="/favicon.png"> that
    // many browsers prefer over a dynamically-added one. We stash them so
    // they can be restored on unmount.
    const removedIcons = [];
    if (favicon) {
      const iconSelectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]',
      ];
      iconSelectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          removedIcons.push({
            rel: el.getAttribute("rel"),
            href: el.getAttribute("href"),
            type: el.getAttribute("type"),
            sizes: el.getAttribute("sizes"),
          });
          el.remove();
        });
      });
      const addIcon = (rel) => {
        const link = document.createElement("link");
        link.setAttribute("rel", rel);
        link.setAttribute("href", favicon);
        link.dataset.dwrrahFavicon = "1";
        document.head.appendChild(link);
      };
      addIcon("icon");
      addIcon("shortcut icon");
      addIcon("apple-touch-icon");
    }

    // Locale + keywords + robots
    if (locale) setMeta("og:locale", locale);
    if (keywords) setMeta("keywords", keywords);
    if (noindex) setMeta("robots", "noindex, nofollow");

    // Phase 6 — theme-color (mobile address-bar color) follows academy primary.
    if (themeColor) setMeta("theme-color", themeColor);

    // hreflang alternates: [{ hreflang: "ar", href: "/ar/..." }, ...]
    const createdAlternates = [];
    if (Array.isArray(alternates) && alternates.length > 0) {
      const base = window.location.origin;
      alternates.forEach((alt) => {
        if (!alt?.hreflang || !alt?.href) return;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", alt.hreflang);
        link.setAttribute("href", alt.href.startsWith("http") ? alt.href : `${base}${alt.href}`);
        link.dataset.dwrrahAlt = "1";
        document.head.appendChild(link);
        createdAlternates.push(link);
      });
    }

    // Page-scoped JSON-LD (single object or array)
    const createdJsonLd = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((obj) => {
        if (!obj) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.dwrrahJsonld = "1";
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
        createdJsonLd.push(s);
      });
    }

    // Cleanup — restore previous values or remove created elements
    return () => {
      document.title = originalTitle;
      const selectors = [
        'meta[name="description"]',
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[property="og:image"]',
        'meta[property="og:type"]',
        'meta[property="og:site_name"]',
        'meta[property="og:url"]',
        'meta[property="og:locale"]',
        'meta[name="keywords"]',
        'meta[name="robots"]',
        'meta[name="twitter:card"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'meta[name="twitter:image"]',
        'link[rel="canonical"]',
      ];
      selectors.forEach((sel) => {
        const el = document.querySelector(sel);
        if (!el) return;
        if (el._created) {
          el.remove();
        } else if (el._prev !== undefined) {
          const attr = sel.includes("link") ? "href" : "content";
          if (el._prev) {
            el.setAttribute(attr, el._prev);
          } else {
            el.removeAttribute(attr);
          }
          delete el._prev;
        }
      });
      // Restore original favicon links if we replaced them
      if (removedIcons.length > 0) {
        document
          .querySelectorAll('link[data-dwrrah-favicon="1"]')
          .forEach((el) => el.remove());
        removedIcons.forEach((info) => {
          const link = document.createElement("link");
          if (info.rel) link.setAttribute("rel", info.rel);
          if (info.href) link.setAttribute("href", info.href);
          if (info.type) link.setAttribute("type", info.type);
          if (info.sizes) link.setAttribute("sizes", info.sizes);
          document.head.appendChild(link);
        });
      }
      createdAlternates.forEach((el) => el.remove());
      createdJsonLd.forEach((el) => el.remove());
    };
  }, [title, description, image, canonicalPath, canonicalUrl, siteName, type, favicon, locale, keywords, noindex, JSON.stringify(alternates || null), JSON.stringify(jsonLd || null)]);
}
